/**
 * Order API Client Service for frontend-v2
 * Implements Milestone M5: R5 Checkout & Order Screen
 * 
 * Endpoints:
 * - POST /api/orders/ : Create order (Transferencia Bancaria or Saldo Billetera)
 * - GET  /api/orders/{order_code} : Retrieve order details, polling & status
 * - POST /api/orders/{order_code}/upload-receipt : Multipart file upload with 5MB validation
 * - POST /api/orders/{order_code}/cancel : Cancel pending order
 * - POST /api/orders/{order_code}/report-paid : Report transfer made without receipt file
 * - GET  /api/orders/my-orders : Fetch user order history
 */

import { SupportedCurrency } from '../types';
import { sanitizeCurrency } from '../utils/currency';

export const MAX_RECEIPT_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
export const ALLOWED_RECEIPT_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.pdf'];
export const ALLOWED_RECEIPT_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
];

export type OrderPaymentMethod = 'TRANSFERENCIA_BANCARIA' | 'SALDO_BILLETERA';

export type OrderStatus =
  | 'PENDIENTE_PAGO'
  | 'PAGO_INFORMADO'
  | 'APROBADO_ENVIANDO'
  | 'COMPLETADO'
  | 'CANCELADO'
  | 'EXPIRADO'
  | 'FALLO_ENVIO';

export interface OrderItemPayload {
  offer_id?: string;
  offerId?: string;
  name: string;
  vbucks: number;
  price?: number;
  image?: string;
  image_url?: string;
}

export interface CreateOrderPayload {
  customer_username: string;
  customer_account_id?: string | null;
  currency: SupportedCurrency;
  payment_method: OrderPaymentMethod;
  items: OrderItemPayload[];
  use_wallet_balance?: boolean;
  discount_code?: string | null;
  customer_email?: string | null;
}

export interface BankTransferDetails {
  alias?: string | null;
  cbu?: string | null;
  clabe?: string | null;
  holder: string;
  bank: string;
  order_code: string;
  amount: number;
  currency: string;
}

export interface OrderItemDetail {
  offer_id?: string;
  offerId?: string;
  name: string;
  vbucks: number;
  price?: number;
  image?: string;
  image_url?: string;
  assigned_bot_id?: number;
  assigned_bot_name?: string;
}

export interface CartSummaryDetail {
  vbucks: number;
  currency: string;
  base_price: number;
  discount_pct: number;
  discount_amount: number;
  final_price: number;
}

export interface OrderDetail {
  id?: number;
  order_code: string;
  customer_username: string;
  customer_epic_id?: string;
  total_vbucks: number;
  total_price: number;
  currency: SupportedCurrency;
  payment_method: OrderPaymentMethod;
  status: OrderStatus;
  created_at: string;
  expires_at: string;
  remaining_seconds: number;
  receipt_url?: string | null;
  wallet_vbucks_used?: number;
  discount_code?: string | null;
  discount_pct?: number;
  discount_amount?: number;
  original_price?: number;
  cancelled_by?: string | null;
  customer_email?: string | null;
  items: OrderItemDetail[];
  bank_details?: BankTransferDetails | null;
  cart_summary?: CartSummaryDetail | null;
}

export interface OrderSummary {
  order_code: string;
  customer_username: string;
  total_vbucks: number;
  total_price: number;
  currency: SupportedCurrency;
  payment_method: OrderPaymentMethod;
  status: OrderStatus;
  created_at: string;
  expires_at?: string;
  items_count: number;
  receipt_url?: string | null;
}

export interface UploadReceiptResponse {
  message: string;
  receipt_url: string;
  status: OrderStatus;
}

export interface CancelOrderResponse {
  message: string;
  order_code: string;
  status: OrderStatus;
  cancelled_by?: string | null;
}

export interface OrderRequestOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
  token?: string | null;
}

export interface UploadReceiptOptions extends OrderRequestOptions {
  onProgress?: (percent: number) => void;
}

/**
 * Structured typed error for all Order API interactions.
 */
export class OrderApiError extends Error {
  public status?: number;
  public code?: string;
  public detail?: any;
  public retryAfter?: number;

  constructor(message: string, status?: number, code?: string, detail?: any, retryAfter?: number) {
    super(message);
    this.name = 'OrderApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.retryAfter = retryAfter;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isFileTooLarge(): boolean {
    return this.status === 413 || this.code === 'FILE_TOO_LARGE';
  }

  get isEligibilityError(): boolean {
    return (
      this.code === 'NOT_ELIGIBLE' ||
      (typeof this.detail === 'object' && this.detail?.error === 'NOT_ELIGIBLE')
    );
  }
}

/**
 * Cleans an order code by trimming whitespace and removing leading '#' characters.
 */
export function cleanOrderCode(rawCode: string): string {
  if (!rawCode || typeof rawCode !== 'string') return '';
  return rawCode.trim().replace(/^#+/, '').trim();
}

/**
 * Retrieves the stored auth token from localStorage if running in browser environment.
 */
export function getStoredAuthToken(): string | null {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return (
        window.localStorage.getItem('fn_token') ||
        window.localStorage.getItem('token') ||
        window.localStorage.getItem('auth_token') ||
        null
      );
    }
  } catch {
    // Storage access restricted
  }
  return null;
}

/**
 * Validates a receipt file before uploading.
 * Checks file presence, non-zero size, size (<= 5MB), and allowed MIME/extension.
 */
export function validateReceiptFile(file: File | Blob): void {
  if (!file) {
    throw new OrderApiError('Debes seleccionar un comprobante para subir.', 400, 'NO_FILE_SELECTED');
  }

  if (!file.size || file.size <= 0) {
    throw new OrderApiError('El archivo está vacío.', 400, 'EMPTY_FILE');
  }

  if (file.size > MAX_RECEIPT_SIZE_BYTES) {
    throw new OrderApiError(
      'El archivo excede el tamaño máximo permitido de 10 MB.',
      413,
      'FILE_TOO_LARGE'
    );
  }

  const fileName = 'name' in file && typeof (file as any).name === 'string' ? (file as File).name.toLowerCase() : '';
  const fileType = file.type && typeof file.type === 'string' ? file.type.toLowerCase().trim() : '';

  const hasExt = fileName.includes('.');
  const hasValidExt = hasExt && ALLOWED_RECEIPT_EXTENSIONS.some((ext) => fileName.endsWith(ext));
  const hasValidMime = Boolean(fileType && ALLOWED_RECEIPT_MIME_TYPES.includes(fileType));

  // If filename has an extension, it MUST be an allowed receipt extension
  if (hasExt && !hasValidExt) {
    throw new OrderApiError(
      'Formato de archivo inválido. Sube JPG, PNG, WEBP o PDF.',
      400,
      'INVALID_FILE_TYPE'
    );
  }

  // If MIME type is specified, it MUST be an allowed receipt MIME type
  if (fileType && !hasValidMime) {
    throw new OrderApiError(
      'Formato de archivo inválido. Sube JPG, PNG, WEBP o PDF.',
      400,
      'INVALID_FILE_TYPE'
    );
  }

  // If neither a valid extension nor a valid MIME type was established, reject
  if (!hasValidExt && !hasValidMime) {
    throw new OrderApiError(
      'Formato de archivo inválido. Sube JPG, PNG, WEBP o PDF.',
      400,
      'INVALID_FILE_TYPE'
    );
  }
}

/**
 * Helper to build headers including optional Bearer token and JSON Accept.
 */
function buildHeaders(options?: OrderRequestOptions, isJson = true): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (isJson) {
    headers['Content-Type'] = 'application/json';
  }
  const token = options?.token ?? getStoredAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Sets up AbortController with configurable timeout.
 */
function setupAbortController(options?: OrderRequestOptions): {
  controller: AbortController;
  cleanup: () => void;
} {
  const controller = new AbortController();
  const timeoutMs = options?.timeoutMs ?? 15000;
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let removeExternalListener: (() => void) | null = null;
  if (options?.signal) {
    if (options.signal.aborted) {
      clearTimeout(timer);
      controller.abort();
    } else {
      const onExternalAbort = () => controller.abort();
      options.signal.addEventListener('abort', onExternalAbort, { once: true });
      removeExternalListener = () => options.signal?.removeEventListener('abort', onExternalAbort);
    }
  }

  const cleanup = () => {
    clearTimeout(timer);
    if (removeExternalListener) removeExternalListener();
  };

  return { controller, cleanup };
}

/**
 * Parses HTTP error response into a typed OrderApiError.
 */
async function handleHttpError(response: Response): Promise<never> {
  let errorMessage = `Error HTTP ${response.status}`;
  let errorDetail: any = null;
  let errorCode: string | undefined = undefined;

  let retryAfter: number | undefined = undefined;
  const retryHeader = response.headers.get('Retry-After');
  if (retryHeader) {
    const parsed = parseInt(retryHeader, 10);
    if (!isNaN(parsed)) retryAfter = parsed;
  }

  try {
    const errorJson = await response.json();
    if (errorJson) {
      errorDetail = errorJson.detail ?? errorJson.message ?? errorJson;
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (typeof errorDetail === 'object') {
        if (errorDetail.error) errorCode = errorDetail.error;
        if (errorDetail.message) errorMessage = errorDetail.message;
        else errorMessage = JSON.stringify(errorDetail);
      }
    }
  } catch {
    // Non-JSON response
  }

  if (response.status === 400) {
    if (!errorCode) errorCode = 'BAD_REQUEST';
    if (!errorMessage || errorMessage.startsWith('Error HTTP')) {
      errorMessage = 'Solicitud de pedido no válida. Revisa los datos ingresados.';
    }
  } else if (response.status === 401) {
    errorCode = 'UNAUTHORIZED';
    errorMessage = 'Debes iniciar sesión con tu cuenta para continuar con este pedido.';
  } else if (response.status === 404) {
    errorCode = 'ORDER_NOT_FOUND';
    errorMessage = 'Pedido no encontrado.';
  } else if (response.status === 413) {
    errorCode = 'FILE_TOO_LARGE';
    errorMessage = 'El archivo excede el tamaño máximo permitido de 5 MB.';
  } else if (response.status === 429) {
    errorCode = 'RATE_LIMITED';
    errorMessage =
      errorMessage && !errorMessage.startsWith('Error HTTP')
        ? errorMessage
        : 'Demasiadas solicitudes. Espera un momento antes de volver a intentar.';
  }

  throw new OrderApiError(errorMessage, response.status, errorCode, errorDetail, retryAfter);
}

/**
 * POST /api/orders/
 * Creates a new order in the backend.
 */
export async function createOrder(
  payload: CreateOrderPayload,
  options?: OrderRequestOptions
): Promise<OrderDetail> {
  if (
    !payload.customer_username ||
    typeof payload.customer_username !== 'string' ||
    payload.customer_username.trim().length < 3
  ) {
    throw new OrderApiError(
      'El nombre de usuario de Fortnite debe tener al menos 3 caracteres.',
      400,
      'INVALID_USERNAME'
    );
  }

  if (!payload.items || !Array.isArray(payload.items) || payload.items.length === 0) {
    throw new OrderApiError('El carrito no puede estar vacío.', 400, 'EMPTY_CART');
  }

  const safeCurrency = sanitizeCurrency(payload.currency);
  const { controller, cleanup } = setupAbortController(options);

  const requestBody: Record<string, any> = {
    customer_username: payload.customer_username.trim(),
    currency: safeCurrency,
    payment_method: payload.payment_method,
    items: payload.items.map((it) => ({
      offer_id: it.offer_id || it.offerId || '',
      offerId: it.offer_id || it.offerId || '',
      name: it.name,
      vbucks: Math.max(0, Math.round(Number(it.vbucks) || 0)),
      price: it.price !== undefined && !isNaN(Number(it.price)) ? Number(it.price) : 0.0,
      image: it.image || it.image_url || null,
      image_url: it.image || it.image_url || null,
    })),
    use_wallet_balance: Boolean(payload.use_wallet_balance),
  };

  if (payload.customer_account_id && payload.customer_account_id.trim()) {
    requestBody.customer_account_id = payload.customer_account_id.trim();
  }
  if (payload.discount_code && payload.discount_code.trim()) {
    requestBody.discount_code = payload.discount_code.trim().toUpperCase();
  }
  if (payload.customer_email && payload.customer_email.trim()) {
    requestBody.customer_email = payload.customer_email.trim();
  }

  try {
    const res = await fetch('/api/orders/', {
      method: 'POST',
      headers: buildHeaders(options, true),
      body: JSON.stringify(requestBody),
      signal: controller.signal,
    });

    cleanup();

    if (!res.ok) {
      await handleHttpError(res);
    }

    const data = await res.json();
    return normalizeOrderDetail(data, safeCurrency);
  } catch (err: any) {
    cleanup();
    if (err?.name === 'AbortError') {
      throw new OrderApiError('Tiempo de espera agotado al generar el pedido.', 408, 'TIMEOUT');
    }
    if (err instanceof OrderApiError) {
      throw err;
    }
    throw new OrderApiError(err?.message || 'Error de conexión al generar el pedido.', undefined, 'NETWORK_ERROR');
  }
}

/**
 * GET /api/orders/{order_code}
 * Retrieves details and remaining countdown for an order by its code.
 */
export async function getOrder(
  orderCode: string,
  options?: OrderRequestOptions
): Promise<OrderDetail> {
  const cleanCode = cleanOrderCode(orderCode);
  if (!cleanCode) {
    throw new OrderApiError('Código de pedido no válido o ausente.', 400, 'INVALID_CODE');
  }

  const { controller, cleanup } = setupAbortController(options);

  try {
    let url = `/api/orders/${encodeURIComponent(cleanCode)}`;
    const urlToken =
      typeof window !== 'undefined' && window.location
        ? new URLSearchParams(window.location.search).get('token')
        : null;
    const tokenQuery = (options as any)?.tokenQuery || urlToken;
    if (tokenQuery) {
      url += `?token=${encodeURIComponent(tokenQuery)}`;
    }

    const res = await fetch(url, {
      method: 'GET',
      headers: buildHeaders(options, false),
      signal: controller.signal,
    });

    cleanup();

    if (!res.ok) {
      await handleHttpError(res);
    }

    const data = await res.json();
    return normalizeOrderDetail(data);
  } catch (err: any) {
    cleanup();
    if (err?.name === 'AbortError') {
      throw new OrderApiError('Tiempo de espera agotado al consultar el pedido.', 408, 'TIMEOUT');
    }
    if (err instanceof OrderApiError) {
      throw err;
    }
    throw new OrderApiError(err?.message || 'Error de conexión al consultar el pedido.', undefined, 'NETWORK_ERROR');
  }
}

/**
 * POST /api/orders/{order_code}/upload-receipt
 * Uploads a payment proof receipt (image or PDF, max 5MB) using multipart/form-data.
 */
export async function uploadReceipt(
  orderCode: string,
  file: File | Blob,
  options?: UploadReceiptOptions
): Promise<UploadReceiptResponse> {
  const cleanCode = cleanOrderCode(orderCode);
  if (!cleanCode) {
    throw new OrderApiError('Código de pedido no válido.', 400, 'INVALID_CODE');
  }

  validateReceiptFile(file);

  const { controller, cleanup } = setupAbortController(options);

  // If running in browser and upload progress callback is provided, XMLHttpRequest can be used
  if (
    typeof window !== 'undefined' &&
    typeof XMLHttpRequest !== 'undefined' &&
    typeof options?.onProgress === 'function'
  ) {
    return new Promise<UploadReceiptResponse>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = `/api/orders/${encodeURIComponent(cleanCode)}/upload-receipt`;

      xhr.open('POST', url, true);
      const token = options?.token ?? getStoredAuthToken();
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.setRequestHeader('Accept', 'application/json');

      if (options?.timeoutMs) {
        xhr.timeout = options.timeoutMs;
      }

      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable && options.onProgress) {
          const percent = Math.round((evt.loaded / evt.total) * 100);
          options.onProgress(percent);
        }
      };

      xhr.onload = () => {
        cleanup();
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({
              message: String(data.message || 'Comprobante subido con éxito.'),
              receipt_url: String(data.receipt_url || ''),
              status: (data.status as OrderStatus) || 'PAGO_INFORMADO',
            });
          } catch {
            resolve({
              message: 'Comprobante subido con éxito.',
              receipt_url: '',
              status: 'PAGO_INFORMADO',
            });
          }
        } else {
          let errDetail: any = xhr.responseText;
          try {
            errDetail = JSON.parse(xhr.responseText);
          } catch {}
          const msg =
            typeof errDetail?.detail === 'string'
              ? errDetail.detail
              : xhr.status === 413
              ? 'El archivo excede el tamaño máximo permitido de 5 MB.'
              : 'Error al subir comprobante de pago.';
          reject(new OrderApiError(msg, xhr.status, xhr.status === 413 ? 'FILE_TOO_LARGE' : undefined, errDetail));
        }
      };

      xhr.onerror = () => {
        cleanup();
        reject(new OrderApiError('Error de red al subir el comprobante de pago.', undefined, 'NETWORK_ERROR'));
      };

      xhr.ontimeout = () => {
        cleanup();
        reject(new OrderApiError('Tiempo de espera agotado al subir el comprobante.', 408, 'TIMEOUT'));
      };

      if (options?.signal) {
        if (options.signal.aborted) {
          cleanup();
          reject(new OrderApiError('Subida cancelada.', 408, 'ABORTED'));
          return;
        }
        options.signal.addEventListener('abort', () => {
          cleanup();
          xhr.abort();
          reject(new OrderApiError('Subida cancelada.', 408, 'ABORTED'));
        }, { once: true });
      }

      const formData = new FormData();
      formData.append('file', file);
      const urlToken =
        typeof window !== 'undefined' && window.location
          ? new URLSearchParams(window.location.search).get('token')
          : null;
      const tokenVal = (options as any)?.tokenQuery || urlToken;
      if (tokenVal) {
        formData.append('token', tokenVal);
      }
      xhr.send(formData);
    });
  }

  // Fallback to fetch for standard environments or headless unit tests
  const formData = new FormData();
  formData.append('file', file);
  const fallbackUrlToken =
    typeof window !== 'undefined' && window.location
      ? new URLSearchParams(window.location.search).get('token')
      : null;
  const fallbackTokenVal = (options as any)?.tokenQuery || fallbackUrlToken;
  if (fallbackTokenVal) {
    formData.append('token', fallbackTokenVal);
  }

  const headers = buildHeaders(options, false); // Don't set Content-Type so browser sets boundary!

  try {
    const res = await fetch(`/api/orders/${encodeURIComponent(cleanCode)}/upload-receipt`, {
      method: 'POST',
      headers,
      body: formData,
      signal: controller.signal,
    });

    cleanup();

    if (!res.ok) {
      await handleHttpError(res);
    }

    const data = await res.json();
    if (options?.onProgress) options.onProgress(100);

    return {
      message: String(data.message || 'Comprobante de pago subido con éxito.'),
      receipt_url: String(data.receipt_url || ''),
      status: (data.status as OrderStatus) || 'PAGO_INFORMADO',
    };
  } catch (err: any) {
    cleanup();
    if (err?.name === 'AbortError') {
      throw new OrderApiError('Tiempo de espera agotado al subir el comprobante.', 408, 'TIMEOUT');
    }
    if (err instanceof OrderApiError) {
      throw err;
    }
    throw new OrderApiError(err?.message || 'Error de conexión al subir el comprobante.', undefined, 'NETWORK_ERROR');
  }
}

/**
 * POST /api/orders/{order_code}/cancel
 * Cancels a pending order.
 */
export async function cancelOrder(
  orderCode: string,
  options?: OrderRequestOptions
): Promise<CancelOrderResponse> {
  const cleanCode = cleanOrderCode(orderCode);
  if (!cleanCode) {
    throw new OrderApiError('Código de pedido no válido.', 400, 'INVALID_CODE');
  }

  const { controller, cleanup } = setupAbortController(options);

  try {
    let url = `/api/orders/${encodeURIComponent(cleanCode)}/cancel`;
    const urlToken =
      typeof window !== 'undefined' && window.location
        ? new URLSearchParams(window.location.search).get('token')
        : null;
    const tokenVal = (options as any)?.tokenQuery || urlToken;
    if (tokenVal) {
      url += `?token=${encodeURIComponent(tokenVal)}`;
    }

    const res = await fetch(url, {
      method: 'POST',
      headers: buildHeaders(options, false),
      signal: controller.signal,
    });

    cleanup();

    if (!res.ok) {
      await handleHttpError(res);
    }

    const data = await res.json();
    return {
      message: String(data.message || 'Pedido cancelado con éxito.'),
      order_code: String(data.order_code || cleanCode),
      status: (data.status as OrderStatus) || 'CANCELADO',
      cancelled_by: data.cancelled_by ? String(data.cancelled_by) : null,
    };
  } catch (err: any) {
    cleanup();
    if (err?.name === 'AbortError') {
      throw new OrderApiError('Tiempo de espera agotado al cancelar el pedido.', 408, 'TIMEOUT');
    }
    if (err instanceof OrderApiError) {
      throw err;
    }
    throw new OrderApiError(err?.message || 'Error de conexión al cancelar el pedido.', undefined, 'NETWORK_ERROR');
  }
}

/**
 * POST /api/orders/{order_code}/report-paid
 * Marks order as paid without an uploaded receipt file.
 */
export async function reportOrderPaid(
  orderCode: string,
  options?: OrderRequestOptions
): Promise<{ message: string; status: OrderStatus; receipt_url?: string | null }> {
  const cleanCode = cleanOrderCode(orderCode);
  if (!cleanCode) {
    throw new OrderApiError('Código de pedido no válido.', 400, 'INVALID_CODE');
  }

  const { controller, cleanup } = setupAbortController(options);

  try {
    const res = await fetch(`/api/orders/${encodeURIComponent(cleanCode)}/report-paid`, {
      method: 'POST',
      headers: buildHeaders(options, false),
      signal: controller.signal,
    });

    cleanup();

    if (!res.ok) {
      await handleHttpError(res);
    }

    const data = await res.json();
    return {
      message: String(data.message || 'Pago informado con éxito.'),
      status: (data.status as OrderStatus) || 'PAGO_INFORMADO',
      receipt_url: data.receipt_url || null,
    };
  } catch (err: any) {
    cleanup();
    if (err instanceof OrderApiError) throw err;
    throw new OrderApiError(err?.message || 'Error al informar el pago.', undefined, 'NETWORK_ERROR');
  }
}

/**
 * GET /api/orders/my-orders
 * Retrieves all orders for the currently authenticated user.
 */
export async function getMyOrders(options?: OrderRequestOptions): Promise<OrderDetail[]> {
  const { controller, cleanup } = setupAbortController(options);

  try {
    const res = await fetch('/api/orders/my-orders', {
      method: 'GET',
      headers: buildHeaders(options, false),
      signal: controller.signal,
    });

    cleanup();

    if (!res.ok) {
      await handleHttpError(res);
    }

    const data = await res.json();
    if (!Array.isArray(data)) return [];
    return data.map((item) => normalizeOrderDetail(item));
  } catch (err: any) {
    cleanup();
    if (err instanceof OrderApiError) throw err;
    throw new OrderApiError(err?.message || 'Error al obtener el historial de pedidos.', undefined, 'NETWORK_ERROR');
  }
}

/**
 * Normalizes raw backend order response into a consistent typed OrderDetail.
 */
export function normalizeOrderDetail(
  raw: any,
  fallbackCurrency: SupportedCurrency = 'ARS'
): OrderDetail {
  if (!raw || typeof raw !== 'object') {
    throw new OrderApiError('Estructura de respuesta de orden no válida.', 500, 'INVALID_RESPONSE');
  }

  const safeCurrency = sanitizeCurrency(raw.currency || fallbackCurrency);

  const items: OrderItemDetail[] = Array.isArray(raw.items)
    ? raw.items.map((it: any) => ({
        offer_id: it?.offer_id || it?.offerId || '',
        offerId: it?.offer_id || it?.offerId || '',
        name: String(it?.name || 'Cosmético'),
        vbucks: Number(it?.vbucks || 0),
        price: it?.price !== undefined ? Number(it?.price) : undefined,
        image: it?.image || it?.image_url || undefined,
        image_url: it?.image_url || it?.image || undefined,
        assigned_bot_id: it?.assigned_bot_id ? Number(it.assigned_bot_id) : undefined,
        assigned_bot_name: it?.assigned_bot_name ? String(it.assigned_bot_name) : undefined,
      }))
    : [];

  let bankDetails: BankTransferDetails | null = null;
  if (raw.bank_details && typeof raw.bank_details === 'object') {
    bankDetails = {
      alias: raw.bank_details.alias || null,
      cbu: raw.bank_details.cbu || null,
      clabe: raw.bank_details.clabe || null,
      holder: String(raw.bank_details.holder || ''),
      bank: String(raw.bank_details.bank || ''),
      order_code: String(raw.bank_details.order_code || raw.order_code || ''),
      amount: Number(raw.bank_details.amount ?? raw.total_price ?? 0),
      currency: String(raw.bank_details.currency || safeCurrency),
    };
  }

  return {
    id: raw.id ? Number(raw.id) : undefined,
    order_code: String(raw.order_code || '').trim(),
    customer_username: String(raw.customer_username || '').trim(),
    customer_epic_id: raw.customer_epic_id ? String(raw.customer_epic_id) : undefined,
    total_vbucks: Number(raw.total_vbucks || 0),
    total_price: Number(raw.total_price || 0),
    currency: safeCurrency,
    payment_method: (raw.payment_method as OrderPaymentMethod) || 'TRANSFERENCIA_BANCARIA',
    status: (raw.status as OrderStatus) || 'PENDIENTE_PAGO',
    created_at: typeof raw.created_at === 'string' ? raw.created_at : new Date(raw.created_at || Date.now()).toISOString(),
    expires_at: typeof raw.expires_at === 'string' ? raw.expires_at : new Date(raw.expires_at || Date.now() + 3600000).toISOString(),
    remaining_seconds: typeof raw.remaining_seconds === 'number' ? Math.max(0, Math.round(raw.remaining_seconds)) : 0,
    receipt_url: raw.receipt_url ? String(raw.receipt_url) : null,
    wallet_vbucks_used: Number(raw.wallet_vbucks_used || 0),
    discount_code: raw.discount_code ? String(raw.discount_code) : null,
    discount_pct: Number(raw.discount_pct || 0),
    discount_amount: Number(raw.discount_amount || 0),
    original_price: raw.original_price !== undefined ? Number(raw.original_price) : undefined,
    cancelled_by: raw.cancelled_by ? String(raw.cancelled_by) : null,
    customer_email: raw.customer_email ? String(raw.customer_email) : null,
    items,
    bank_details: bankDetails,
    cart_summary: raw.cart_summary || null,
  };
}

export const orderApi = {
  createOrder,
  getOrder,
  uploadReceipt,
  cancelOrder,
  reportOrderPaid,
  getMyOrders,
  cleanOrderCode,
  validateReceiptFile,
  normalizeOrderDetail,
};

export default orderApi;
